import type { AgentSpec, AltYChatMessage, FlowDoc, FlowStep, ProcessMapDoc, ProcessMapEdge, ProcessMapNode, ProcessMapNodeKind } from '../types/global';

// Coerce a raw `spec` value (from parsed JSON) into an AgentSpec, keeping only
// the string fields. Returns undefined when there's nothing usable so the
// diagram doc stays lean.
function coerceSpec(raw: unknown): AgentSpec | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const spec: AgentSpec = {};
  if (typeof r.description === 'string' && r.description.trim()) spec.description = r.description;
  if (typeof r.original === 'string' && r.original.trim()) spec.original = r.original;
  if (typeof r.clarified === 'string' && r.clarified.trim()) spec.clarified = r.clarified;
  if (typeof r.detailed === 'string' && r.detailed.trim()) spec.detailed = r.detailed;
  return spec.description || spec.original || spec.clarified || spec.detailed ? spec : undefined;
}

// Pull the most recent ```json``` block in chat history that parses as a
// ProcessMapDoc. Mirrors findLatestFlow in App.tsx but for the richer map
// shape — `kind: "process-map"` + `nodes` array distinguishes it from the
// legacy FlowDoc the same agent-factory chats can also emit.
export function findLatestProcessMap(messages: AltYChatMessage[]): ProcessMapDoc | null {
  const re = /```json\s*([\s\S]*?)```/i;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== 'assistant' || !message.text) continue;
    const match = message.text.match(re);
    if (!match) continue;
    try {
      const parsed = JSON.parse(match[1]);
      if (isProcessMap(parsed)) return normaliseProcessMap(parsed);
    } catch {
      /* not a process map — keep scanning */
    }
  }
  return null;
}

export function isProcessMap(value: unknown): value is ProcessMapDoc {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<ProcessMapDoc>;
  if (v.kind !== 'process-map') return false;
  if (typeof v.title !== 'string') return false;
  if (!Array.isArray(v.nodes) || !Array.isArray(v.edges)) return false;
  return true;
}

const VALID_KINDS: ReadonlySet<ProcessMapNodeKind> = new Set([
  'box', 'diamond', 'circle', 'text', 'start', 'end', 'chevron', 'document', 'actor', 'warning', 'bracket', 'milestone',
]);

// Defensive coerce: fill in missing geometry, drop edges that reference
// unknown nodes, clamp dimensions so a malformed payload still renders.
export function normaliseProcessMap(doc: ProcessMapDoc): ProcessMapDoc {
  const nodes: ProcessMapNode[] = [];
  let i = 0;
  for (const raw of doc.nodes || []) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as Partial<ProcessMapNode>;
    const id = typeof r.id === 'string' && r.id ? r.id : `n${i + 1}`;
    const kind = VALID_KINDS.has(r.kind as ProcessMapNodeKind) ? r.kind as ProcessMapNodeKind : 'box';
    const x = Number.isFinite(r.x) ? Number(r.x) : 100 + (i % 4) * 220;
    const y = Number.isFinite(r.y) ? Number(r.y) : 120 + Math.floor(i / 4) * 140;
    const w = Number.isFinite(r.w) ? Math.max(30, Number(r.w)) : kindDefaults(kind).w;
    const h = Number.isFinite(r.h) ? Math.max(30, Number(r.h)) : kindDefaults(kind).h;
    const text = typeof r.text === 'string' ? r.text : '';
    const fill = typeof r.fill === 'string' ? r.fill : undefined;
    const stroke = typeof r.stroke === 'string' ? r.stroke : undefined;
    nodes.push({ id, kind, x, y, w, h, text, fill, stroke });
    i += 1;
  }
  const validIds = new Set(nodes.map((n) => n.id));
  const edges: ProcessMapEdge[] = [];
  let j = 0;
  for (const raw of doc.edges || []) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as Partial<ProcessMapEdge>;
    if (typeof r.from !== 'string' || typeof r.to !== 'string') continue;
    if (!validIds.has(r.from) || !validIds.has(r.to)) continue;
    edges.push({
      id: typeof r.id === 'string' && r.id ? r.id : `e${j + 1}`,
      from: r.from,
      to: r.to,
      label: typeof r.label === 'string' ? r.label : undefined,
      dashed: r.dashed === true,
    });
    j += 1;
  }
  const imageUrl = typeof doc.imageUrl === 'string' && doc.imageUrl.trim() ? doc.imageUrl.trim() : undefined;
  return {
    kind: 'process-map',
    title: doc.title || 'Untitled process',
    description: typeof doc.description === 'string' ? doc.description : undefined,
    nodes,
    edges,
    spec: coerceSpec(doc.spec),
    ...(imageUrl ? { imageUrl } : {}),
  };
}

export function kindDefaults(kind: ProcessMapNodeKind): { w: number; h: number } {
  switch (kind) {
    case 'circle':
    case 'start':
    case 'end':
      return { w: 110, h: 110 };
    case 'diamond':
      return { w: 200, h: 110 };
    case 'text':
      return { w: 180, h: 40 };
    case 'chevron':
      return { w: 200, h: 70 };
    case 'document':
      return { w: 100, h: 80 };
    case 'actor':
      return { w: 100, h: 80 };
    case 'warning':
      return { w: 100, h: 80 };
    case 'bracket':
      return { w: 180, h: 80 };
    case 'milestone':
      return { w: 100, h: 80 };
    default:
      return { w: 200, h: 70 };
  }
}

export function createNode(kind: ProcessMapNodeKind, x: number, y: number, text = ''): ProcessMapNode {
  const def = kindDefaults(kind);
  return {
    id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    kind,
    x: Math.round(x - def.w / 2),
    y: Math.round(y - def.h / 2),
    w: def.w,
    h: def.h,
    text,
  };
}

export function createEdge(from: string, to: string): ProcessMapEdge {
  return { id: `e-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, from, to };
}

// Flatten a process map into a vertical FlowDoc spine. Used when the user
// clicks "Make agent" on a process-map diagram: the agent is stored and its
// pin self-portrait is rendered by FactoryCanvas, which only understands the
// FlowDoc shape. The richer 2D map still reaches the dev-orchestrator turn via
// the chat transcript, so nothing about the design is lost — this is purely the
// pinned agent's at-a-glance summary. Walks the graph from its entry point(s)
// so the spine reads in flow order, falling back to raw node order.
export function processMapToFlow(doc: ProcessMapDoc): FlowDoc {
  const byId = new Map(doc.nodes.map((n) => [n.id, n] as const));
  const outgoing = new Map<string, string[]>();
  const indeg = new Map<string, number>();
  for (const n of doc.nodes) indeg.set(n.id, 0);
  for (const e of doc.edges) {
    if (!byId.has(e.from) || !byId.has(e.to)) continue;
    const list = outgoing.get(e.from);
    if (list) list.push(e.to);
    else outgoing.set(e.from, [e.to]);
    indeg.set(e.to, (indeg.get(e.to) ?? 0) + 1);
  }
  const order: string[] = [];
  const seen = new Set<string>();
  const roots = doc.nodes.filter((n) => n.kind === 'start' || (indeg.get(n.id) ?? 0) === 0);
  const queue = (roots.length ? roots : doc.nodes).map((n) => n.id);
  while (queue.length) {
    const id = queue.shift() as string;
    if (seen.has(id)) continue;
    seen.add(id);
    order.push(id);
    for (const next of outgoing.get(id) ?? []) if (!seen.has(next)) queue.push(next);
  }
  for (const n of doc.nodes) if (!seen.has(n.id)) order.push(n.id);

  const steps: FlowStep[] = [];
  order.forEach((id, idx) => {
    const node = byId.get(id);
    if (!node) return;
    const text = (node.text || '').trim();
    if (!text) return;
    const isEntry = idx === 0 && (node.kind === 'start' || (indeg.get(id) ?? 0) === 0);
    steps.push({
      kind: isEntry ? 'trigger' : node.kind === 'diamond' ? 'gate' : 'step',
      text,
    });
  });

  return {
    title: doc.title || 'Untitled agent',
    description: doc.description,
    steps,
    // Carry the written brief onto the flattened FlowDoc so the made/pinned
    // agent keeps its original request + clarified + detailed spec.
    ...(doc.spec ? { spec: doc.spec } : {}),
    // Carry the optional GPT-image visualization through to the FlowDoc so
    // the spine renderer can expose the same "Diagram | Visual" toggle.
    ...(doc.imageUrl ? { imageUrl: doc.imageUrl } : {}),
  };
}

// Stringify the doc for round-tripping through the AI. Stable key order keeps
// the agent's diffs minimal turn-to-turn.
export function serializeProcessMap(doc: ProcessMapDoc): string {
  return JSON.stringify({
    kind: doc.kind,
    title: doc.title,
    description: doc.description,
    nodes: doc.nodes.map((n) => ({
      id: n.id, kind: n.kind, x: n.x, y: n.y, w: n.w, h: n.h, text: n.text,
      ...(n.fill ? { fill: n.fill } : {}),
      ...(n.stroke ? { stroke: n.stroke } : {}),
    })),
    edges: doc.edges.map((e) => ({
      id: e.id, from: e.from, to: e.to,
      ...(e.label ? { label: e.label } : {}),
      ...(e.dashed ? { dashed: true } : {}),
    })),
    // Round-trip the brief so a manually-edited map fed back to the AI keeps
    // the same spec context to iterate on.
    ...(doc.spec ? { spec: doc.spec } : {}),
    // Round-trip the GPT-image URL too, so the model knows a visual already
    // exists for this iteration and can decide whether to refresh it.
    ...(doc.imageUrl ? { imageUrl: doc.imageUrl } : {}),
  }, null, 2);
}
